<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbs" />

    <!-- Toolbar -->
    <div class="card mb-3">
      <div class="card-body py-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px">
          <div class="cp-search-box">
            <input
              type="text"
              class="form-control"
              v-model="searchQuery"
              placeholder="Buscar templates..."
              @input="debounceSearch"
            />
            <i class="bx bx-search-alt cp-search-icon"></i>
          </div>
          <button type="button" class="btn btn-success" @click="createNew">
            <i class="bx bx-plus mr-1"></i> Novo Template
          </button>
        </div>
      </div>
    </div>

    <!-- Templates Table -->
    <div class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Descrição</th>
                <th>Blocos</th>
                <th>Status</th>
                <th class="text-center" style="width: 150px">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="5" class="text-center py-4">
                  <b-spinner variant="primary" small></b-spinner>
                  <span class="ml-2">Carregando...</span>
                </td>
              </tr>
              <tr v-else-if="templates.length === 0">
                <td colspan="5" class="text-center py-4 text-muted">
                  Nenhum template encontrado.
                </td>
              </tr>
              <tr v-for="tpl in templates" :key="tpl.id">
                <td>
                  <strong>{{ tpl.name }}</strong>
                </td>
                <td>
                  <span class="text-muted">{{ tpl.description || '—' }}</span>
                </td>
                <td>
                  <span class="badge badge-soft-primary">
                    {{ (tpl.blocks || []).length }} bloco(s)
                  </span>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="tpl.is_active ? 'badge-soft-success' : 'badge-soft-secondary'"
                  >
                    {{ tpl.is_active ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="text-center">
                  <b-dropdown variant="link" toggle-class="p-0 text-muted" right no-caret>
                    <template v-slot:button-content>
                      <i class="bx bx-dots-vertical-rounded font-size-18"></i>
                    </template>
                    <b-dropdown-item @click="editTemplate(tpl.id)">
                      <i class="bx bx-edit mr-1"></i> Editar
                    </b-dropdown-item>
                    <b-dropdown-item @click="openGenerate(tpl)">
                      <i class="bx bx-file mr-1"></i> Gerar PDFs
                    </b-dropdown-item>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-item @click="duplicateTemplate(tpl)">
                      <i class="bx bx-copy mr-1"></i> Duplicar
                    </b-dropdown-item>
                    <b-dropdown-item variant="danger" @click="deleteTemplate(tpl)">
                      <i class="bx bx-trash mr-1"></i> Excluir
                    </b-dropdown-item>
                  </b-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-end mt-3" v-if="pagination.total > 20">
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.total"
            :per-page="20"
            @change="onPageChange"
          ></b-pagination>
        </div>
      </div>
    </div>

    <!-- Generate Modal -->
    <GenerateModal
      v-if="selectedTemplateId"
      :templateId="selectedTemplateId"
      ref="generateModal"
    />
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import GenerateModal from './components/GenerateModal.vue'
import { mapGetters, mapActions } from 'vuex'

let searchTimeout = null

export default {
  name: 'CartaPaxIndex',
  components: {
    Layout,
    PageHeader,
    GenerateModal
  },
  data () {
    return {
      title: 'Carta Pax',
      breadcrumbs: [
        { text: 'Dashboard', href: '/' },
        { text: 'Carta Pax', active: true }
      ],
      searchQuery: '',
      currentPage: 1,
      loading: false,
      selectedTemplateId: null
    }
  },
  computed: {
    ...mapGetters('cartaPax', ['TEMPLATES']),
    templates () {
      return this.TEMPLATES || []
    },
    pagination () {
      return this.$store.state.cartaPax.pagination || {}
    }
  },
  mounted () {
    this.loadTemplates()
  },
  methods: {
    ...mapActions('cartaPax', ['GetTemplates', 'DeleteTemplate', 'DuplicateTemplate']),
    async loadTemplates () {
      this.loading = true
      try {
        await this.GetTemplates({
          page: this.currentPage,
          search: this.searchQuery || undefined,
          limit: 20
        })
      } finally {
        this.loading = false
      }
    },
    debounceSearch () {
      if (searchTimeout) clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.loadTemplates()
      }, 400)
    },
    onPageChange (page) {
      this.currentPage = page
      this.loadTemplates()
    },
    createNew () {
      this.$router.push({ name: 'carta-pax-new' })
    },
    editTemplate (id) {
      this.$router.push({ name: 'carta-pax-edit', params: { id } })
    },
    openGenerate (tpl) {
      this.selectedTemplateId = tpl.id
      this.$nextTick(() => {
        this.$bvModal.show('generate-modal')
      })
    },
    async duplicateTemplate (tpl) {
      try {
        await this.DuplicateTemplate(tpl.id)
        this.$bvToast.toast('Template duplicado com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        this.loadTemplates()
      } catch (e) {
        this.$bvToast.toast('Erro ao duplicar template.', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      }
    },
    async deleteTemplate (tpl) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        `Deseja realmente excluir o template "${tpl.name}"?`,
        {
          title: 'Confirmar exclusão',
          okVariant: 'danger',
          okTitle: 'Excluir',
          cancelTitle: 'Cancelar'
        }
      )
      if (!confirm) return

      try {
        await this.DeleteTemplate(tpl.id)
        this.$bvToast.toast('Template excluído com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        this.loadTemplates()
      } catch (e) {
        this.$bvToast.toast('Erro ao excluir template.', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      }
    }
  }
}
</script>

<style scoped>
.cp-search-box {
  position: relative;
  min-width: 280px;
}
.cp-search-box .form-control {
  padding-left: 36px;
}
.cp-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 16px;
}
.badge-soft-primary {
  background: #e3f2fd;
  color: #1565c0;
}
.badge-soft-success {
  background: #e8f5e9;
  color: #2e7d32;
}
.badge-soft-secondary {
  background: #f5f5f5;
  color: #757575;
}
</style>
